/**
 * Balance General - Finance 360
 * Balance sheet / Statement of financial position
 */

import { useState, useEffect, useRef } from 'react';
import {
  PieChart,
  Calendar,
  RefreshCw,
  Download,
  ChevronRight,
  ChevronDown,
  TrendingUp,
  Wallet,
  Building2,
  CheckCircle2,
  AlertTriangle,
  FileText,
  FileSpreadsheet,
  Wrench,
  X
} from 'lucide-react';
import toast from 'react-hot-toast';
import { contabilidadApi } from '../../utils/api';
import useAuthStore from '../../store/authStore';
import { LoadingPage } from '../../components/LoadingSpinner';
import usePeriodosContables from '../../hooks/usePeriodosContables';
import { ordenarPeriodos, diaDe, hoyCR } from '../../utils/periodosContables';

// Toolbar button component
function ToolbarButton({ icon: Icon, label, onClick, disabled, variant = 'default' }) {
  const baseClasses = "flex flex-col items-center justify-center px-3 py-2 rounded-lg transition-all text-xs font-medium min-w-[70px]";
  const variants = {
    default: "border border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400",
    primary: "border border-blue-400 text-blue-600 hover:bg-blue-50 hover:border-blue-500",
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`${baseClasses} ${variants[variant]} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
    >
      <Icon className="h-5 w-5 mb-1" />
      <span className="whitespace-nowrap">{label}</span>
    </button>
  );
}

function ToolbarSeparator() {
  return <div className="w-px h-12 bg-gray-300 mx-1" />;
}

export default function BalanceGeneral() {
  const { currentEmisor } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [balance, setBalance] = useState(null);
  // Hoy en Costa Rica: con toISOString, después de las 6 p. m. salía mañana.
  const [fecha, setFecha] = useState(hoyCR());
  // Corte al cierre de un período contable, mensual o de 4 semanas (S2-02).
  const periodos = usePeriodosContables();
  const [expandedSections, setExpandedSections] = useState({
    activos: true,
    pasivos: true,
    patrimonio: true
  });
  const [showDiagnostico, setShowDiagnostico] = useState(false);
  const [diagnostico, setDiagnostico] = useState(null);
  const [loadingDiag, setLoadingDiag] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [exporting, setExporting] = useState(false);
  const exportMenuRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(event.target)) {
        setShowExportMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (currentEmisor) {
      loadBalance();
    }
  }, [currentEmisor]);

  const loadBalance = async (corte = fecha) => {
    setLoading(true);
    try {
      const response = await contabilidadApi.getBalanceGeneral({ fecha: corte });
      setBalance(response.data.data);
    } catch (error) {
      console.error('Error loading balance:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadDiagnostico = async () => {
    setLoadingDiag(true);
    try {
      const response = await contabilidadApi.getBalanceDiagnostico({ fecha });
      setDiagnostico(response.data.data);
      setShowDiagnostico(true);
    } catch (error) {
      console.error('Error loading diagnostico:', error);
    } finally {
      setLoadingDiag(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-CR', {
      style: 'currency',
      currency: 'CRC',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleExport = async (format) => {
    setShowExportMenu(false);
    setExporting(true);
    try {
      const response = await contabilidadApi.exportBalanceGeneral({
        fecha,
        format
      });

      const blob = new Blob([response.data], {
        type: format === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const fileName = `Balance_General_${fecha}.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Balance General exportado a ${format.toUpperCase()}`);
    } catch (error) {
      console.error('Error exporting:', error);
      toast.error('Error al exportar el reporte');
    } finally {
      setExporting(false);
    }
  };

  const renderCuentas = (cuentas, level = 0) => {
    return cuentas.map((cuenta) => (
      <div key={cuenta.codigo} className="border-b last:border-0">
        <div
          className={`flex items-center justify-between py-2 hover:bg-gray-50 ${
            level === 0 ? 'px-4' : level === 1 ? 'px-8' : 'px-12'
          }`}
        >
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400 font-mono">{cuenta.codigo}</span>
            <span className={`text-sm ${level === 0 ? 'font-semibold' : ''}`}>
              {cuenta.nombre}
            </span>
          </div>
          <span className={`text-sm font-medium ${
            cuenta.saldo >= 0 ? 'text-gray-900' : 'text-red-600'
          }`}>
            {formatCurrency(Math.abs(cuenta.saldo))}
          </span>
        </div>
        {cuenta.subcuentas && cuenta.subcuentas.length > 0 && (
          <div className="bg-gray-50/50">
            {renderCuentas(cuenta.subcuentas, level + 1)}
          </div>
        )}
      </div>
    ));
  };

  if (loading) {
    return <LoadingPage />;
  }

  if (!balance) {
    return (
      <div className="text-center py-12">
        <PieChart className="h-12 w-12 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-500">No hay datos para mostrar</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="bg-white rounded-lg border p-3 flex items-center justify-center gap-2 flex-wrap">
        <ToolbarButton
          icon={RefreshCw}
          label="Generar"
          onClick={() => loadBalance()}
          disabled={loading}
          variant="primary"
        />

        <ToolbarSeparator />

        <div className="relative" ref={exportMenuRef}>
          <ToolbarButton
            icon={Download}
            label="Exportar"
            onClick={() => setShowExportMenu(!showExportMenu)}
            disabled={exporting || !balance}
          />
          {showExportMenu && (
            <div className="absolute top-full left-0 mt-1 bg-white border rounded-lg shadow-lg z-10 py-1 min-w-[170px] w-max">
              <button
                onClick={() => handleExport('pdf')}
                className="w-full flex items-center gap-2 px-4 py-2 text-sm whitespace-nowrap text-gray-700 hover:bg-gray-100"
              >
                <FileText className="h-4 w-4 text-red-500" />
                Exportar PDF
              </button>
              <button
                onClick={() => handleExport('excel')}
                className="w-full flex items-center gap-2 px-4 py-2 text-sm whitespace-nowrap text-gray-700 hover:bg-gray-100"
              >
                <FileSpreadsheet className="h-4 w-4 text-green-500" />
                Exportar Excel
              </button>
            </div>
          )}
        </div>

        {!balance?.cuadra && (
          <>
            <ToolbarSeparator />
            <ToolbarButton
              icon={Wrench}
              label="Diagnóstico"
              onClick={loadDiagnostico}
              disabled={loadingDiag}
              variant="primary"
            />
          </>
        )}

        <ToolbarSeparator />

        {/* Date selector */}
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 text-gray-400" />
          <input
            type="date"
            value={fecha}
            onChange={(e) => setFecha(e.target.value)}
            className="px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
          />
          {periodos.length > 0 && (
            <select
              data-prueba="corte-periodo"
              value={periodos.find(p => diaDe(p.fechaFin) === fecha)?.id ?? ''}
              onChange={(e) => {
                const p = periodos.find(x => String(x.id) === e.target.value);
                if (p) { setFecha(diaDe(p.fechaFin)); loadBalance(diaDe(p.fechaFin)); }
              }}
              className="px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              title="Corte al último día de un período contable"
            >
              <option value="">Al cierre de…</option>
              {ordenarPeriodos(periodos).reverse().map(p => (
                <option key={p.id} value={p.id}>{p.nombre}</option>
              ))}
            </select>
          )}
        </div>
      </div>

      {/* Balance Status */}
      <div className={`flex items-center gap-3 p-4 rounded-xl ${
        balance.cuadra
          ? 'bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200'
          : 'bg-red-50 border border-red-200'
      }`}>
        {balance.cuadra ? (
          <>
            <div className="h-10 w-10 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle2 className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <span className="text-green-800 font-semibold text-lg">Balance cuadra correctamente</span>
              <p className="text-green-600 text-sm">Activos = Pasivos + Patrimonio</p>
            </div>
          </>
        ) : (
          <>
            <AlertTriangle className="h-5 w-5 text-red-600" />
            <span className="text-red-700 font-medium">
              Balance descuadrado: {formatCurrency(Math.abs(balance.diferencia || 0))}
            </span>
          </>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="h-10 w-10 bg-blue-50 rounded-lg flex items-center justify-center">
              <TrendingUp className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500">Total Activos</p>
              <p className="text-xl font-bold text-gray-900">
                {formatCurrency(balance.activos.total)}
              </p>
            </div>
          </div>
          <p className="text-xs text-gray-500">{balance.activos.cuentas.length} cuentas</p>
        </div>

        <div className="bg-white rounded-xl border p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="h-10 w-10 bg-red-50 rounded-lg flex items-center justify-center">
              <Wallet className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500">Total Pasivos</p>
              <p className="text-xl font-bold text-gray-900">
                {formatCurrency(balance.pasivos.total)}
              </p>
            </div>
          </div>
          <p className="text-xs text-gray-500">{balance.pasivos.cuentas.length} cuentas</p>
        </div>

        <div className="bg-white rounded-xl border p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="h-10 w-10 bg-green-50 rounded-lg flex items-center justify-center">
              <Building2 className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500">Patrimonio</p>
              <p className="text-xl font-bold text-gray-900">
                {formatCurrency(balance.patrimonio.total)}
              </p>
            </div>
          </div>
          <p className="text-xs text-gray-500">
            Incluye utilidad: {formatCurrency(balance.patrimonio.utilidadEjercicio)}
          </p>
        </div>
      </div>

      {/* Balance Detail */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Activos */}
        <div className="bg-white rounded-xl border">
          <button
            onClick={() => toggleSection('activos')}
            className="w-full flex items-center justify-between p-4 border-b hover:bg-gray-50"
          >
            <div className="flex items-center gap-2">
              {expandedSections.activos ? (
                <ChevronDown className="h-5 w-5 text-gray-400" />
              ) : (
                <ChevronRight className="h-5 w-5 text-gray-400" />
              )}
              <h3 className="font-semibold text-gray-900">ACTIVOS</h3>
            </div>
            <span className="font-bold text-blue-600">
              {formatCurrency(balance.activos.total)}
            </span>
          </button>

          {expandedSections.activos && (
            <div className="divide-y">
              {balance.activos.cuentas.map((cuenta) => (
                <div key={cuenta.codigo}>
                  <div className="flex items-center justify-between px-4 py-3 hover:bg-gray-50">
                    <div>
                      <p className="text-xs text-gray-400 font-mono">{cuenta.codigo}</p>
                      <p className="text-sm font-medium">{cuenta.nombre}</p>
                    </div>
                    <span className="font-medium text-gray-900">
                      {formatCurrency(cuenta.saldo)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Pasivos y Patrimonio */}
        <div className="space-y-6">
          {/* Pasivos */}
          <div className="bg-white rounded-xl border">
            <button
              onClick={() => toggleSection('pasivos')}
              className="w-full flex items-center justify-between p-4 border-b hover:bg-gray-50"
            >
              <div className="flex items-center gap-2">
                {expandedSections.pasivos ? (
                  <ChevronDown className="h-5 w-5 text-gray-400" />
                ) : (
                  <ChevronRight className="h-5 w-5 text-gray-400" />
                )}
                <h3 className="font-semibold text-gray-900">PASIVOS</h3>
              </div>
              <span className="font-bold text-red-600">
                {formatCurrency(balance.pasivos.total)}
              </span>
            </button>

            {expandedSections.pasivos && (
              <div className="divide-y">
                {balance.pasivos.cuentas.map((cuenta) => (
                  <div key={cuenta.codigo}>
                    <div className="flex items-center justify-between px-4 py-3 hover:bg-gray-50">
                      <div>
                        <p className="text-xs text-gray-400 font-mono">{cuenta.codigo}</p>
                        <p className="text-sm font-medium">{cuenta.nombre}</p>
                      </div>
                      <span className="font-medium text-gray-900">
                        {formatCurrency(cuenta.saldo)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Patrimonio */}
          <div className="bg-white rounded-xl border">
            <button
              onClick={() => toggleSection('patrimonio')}
              className="w-full flex items-center justify-between p-4 border-b hover:bg-gray-50"
            >
              <div className="flex items-center gap-2">
                {expandedSections.patrimonio ? (
                  <ChevronDown className="h-5 w-5 text-gray-400" />
                ) : (
                  <ChevronRight className="h-5 w-5 text-gray-400" />
                )}
                <h3 className="font-semibold text-gray-900">PATRIMONIO</h3>
              </div>
              <span className="font-bold text-green-600">
                {formatCurrency(balance.patrimonio.total)}
              </span>
            </button>

            {expandedSections.patrimonio && (
              <div className="divide-y">
                {balance.patrimonio.cuentas.map((cuenta) => (
                  <div key={cuenta.codigo}>
                    <div className="flex items-center justify-between px-4 py-3 hover:bg-gray-50">
                      <div>
                        <p className="text-xs text-gray-400 font-mono">{cuenta.codigo}</p>
                        <p className="text-sm font-medium">{cuenta.nombre}</p>
                      </div>
                      <span className="font-medium text-gray-900">
                        {formatCurrency(cuenta.saldo)}
                      </span>
                    </div>
                  </div>
                ))}
                {/* Resultados de años anteriores (sin asiento de cierre anual: se calculan solos) */}
                {!!balance.patrimonio.resultadosAnteriores && (
                  <div className="flex items-center justify-between px-4 py-3">
                    <div>
                      <p className="text-xs text-gray-500 font-mono">-</p>
                      <p className="text-sm font-medium text-gray-700">Resultados de ejercicios anteriores</p>
                    </div>
                    <span className="font-medium text-gray-900">
                      {formatCurrency(balance.patrimonio.resultadosAnteriores)}
                    </span>
                  </div>
                )}
                {/* Utilidad del Ejercicio */}
                <div className="flex items-center justify-between px-4 py-3 bg-green-50">
                  <div>
                    <p className="text-xs text-green-600 font-mono">-</p>
                    <p className="text-sm font-medium text-green-700">Utilidad del Ejercicio</p>
                  </div>
                  <span className="font-medium text-green-700">
                    {formatCurrency(balance.patrimonio.utilidadEjercicio)}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Equation */}
      <div className="bg-gray-50 rounded-xl border p-6">
        <h3 className="font-semibold text-gray-900 mb-4 text-center">Ecuación Contable</h3>
        <div className="flex items-center justify-center gap-4 text-center">
          <div>
            <p className="text-2xl font-bold text-blue-600">{formatCurrency(balance.activos.total)}</p>
            <p className="text-xs text-gray-500 mt-1">ACTIVOS</p>
          </div>
          <span className="text-2xl text-gray-400">=</span>
          <div>
            <p className="text-2xl font-bold text-red-600">{formatCurrency(balance.pasivos.total)}</p>
            <p className="text-xs text-gray-500 mt-1">PASIVOS</p>
          </div>
          <span className="text-2xl text-gray-400">+</span>
          <div>
            <p className="text-2xl font-bold text-green-600">{formatCurrency(balance.patrimonio.total)}</p>
            <p className="text-xs text-gray-500 mt-1">PATRIMONIO</p>
          </div>
        </div>
      </div>

      {/* Modal de Diagnóstico */}
      {showDiagnostico && diagnostico && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="font-semibold text-lg">Diagnóstico del Balance General</h3>
              <button onClick={() => setShowDiagnostico(false)} className="p-1 hover:bg-gray-100 rounded">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-4 overflow-y-auto space-y-4">
              {/* Resumen de ecuación */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium mb-3">Ecuación Contable</h4>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-center text-sm">
                  <div className="bg-white p-2 rounded border">
                    <p className="text-gray-500">Activos</p>
                    <p className="font-bold">{formatCurrency(diagnostico.resumen.totalActivos)}</p>
                  </div>
                  <div className="bg-white p-2 rounded border">
                    <p className="text-gray-500">Pasivos</p>
                    <p className="font-bold">{formatCurrency(diagnostico.resumen.totalPasivos)}</p>
                  </div>
                  <div className="bg-white p-2 rounded border">
                    <p className="text-gray-500">Patrimonio</p>
                    <p className="font-bold">{formatCurrency(diagnostico.resumen.totalPatrimonio)}</p>
                  </div>
                  <div className="bg-white p-2 rounded border">
                    <p className="text-gray-500">Utilidad</p>
                    <p className="font-bold">{formatCurrency(diagnostico.resumen.utilidad)}</p>
                  </div>
                  <div className={`p-2 rounded border ${diagnostico.resumen.ecuacion.cuadra ? 'bg-green-50' : 'bg-red-50'}`}>
                    <p className="text-gray-500">Diferencia</p>
                    <p className={`font-bold ${diagnostico.resumen.ecuacion.cuadra ? 'text-green-600' : 'text-red-600'}`}>
                      {formatCurrency(diagnostico.resumen.ecuacion.diferencia)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Asientos por estado */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium mb-3">Asientos por Estado</h4>
                <div className="grid grid-cols-3 gap-3">
                  {diagnostico.asientosPorEstado.map(item => (
                    <div key={item.estado} className={`p-3 rounded border text-center ${
                      item.estado === 'borrador' ? 'bg-yellow-50 border-yellow-200' :
                      item.estado === 'contabilizado' ? 'bg-green-50 border-green-200' :
                      'bg-gray-50'
                    }`}>
                      <p className="text-xs text-gray-500 uppercase">{item.estado}</p>
                      <p className="text-xl font-bold">{item.cantidad}</p>
                      <p className="text-xs text-gray-400">
                        D: {formatCurrency(item.total_debito)} / C: {formatCurrency(item.total_credito)}
                      </p>
                    </div>
                  ))}
                </div>
                {diagnostico.asientosPorEstado.some(a => a.estado === 'borrador' && a.cantidad > 0) && (
                  <p className="text-yellow-700 text-sm mt-2 bg-yellow-100 p-2 rounded">
                    Los asientos en borrador NO se incluyen en el Balance. Debe contabilizarlos para que se reflejen.
                  </p>
                )}
              </div>

              {/* Problemas detectados */}
              {(diagnostico.problemas.cuentasNoMovimiento > 0 ||
                diagnostico.problemas.cuentasInactivas > 0 ||
                diagnostico.problemas.cuentasExcluidasDelBalance > 0) && (
                <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                  <h4 className="font-medium text-red-800 mb-3">Problemas Detectados</h4>
                  <ul className="space-y-2 text-sm text-red-700">
                    {diagnostico.problemas.cuentasNoMovimiento > 0 && (
                      <li>
                        {diagnostico.problemas.cuentasNoMovimiento} cuenta(s) con movimientos pero marcadas como "no es cuenta de movimiento"
                      </li>
                    )}
                    {diagnostico.problemas.cuentasInactivas > 0 && (
                      <li>
                        {diagnostico.problemas.cuentasInactivas} cuenta(s) inactivas con movimientos
                      </li>
                    )}
                    {diagnostico.problemas.cuentasExcluidasDelBalance > 0 && (
                      <li>
                        {diagnostico.problemas.cuentasExcluidasDelBalance} cuenta(s) de balance excluidas por configuración incorrecta
                      </li>
                    )}
                  </ul>
                </div>
              )}

              {/* Cuentas excluidas */}
              {diagnostico.detalle.cuentasExcluidas.length > 0 && (
                <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                  <h4 className="font-medium text-yellow-800 mb-3">Cuentas Excluidas del Balance</h4>
                  <p className="text-sm text-yellow-700 mb-2">
                    Estas cuentas tienen movimientos pero no aparecen en el Balance porque no están marcadas como "cuenta de movimiento" o están inactivas:
                  </p>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-yellow-200">
                        <th className="text-left py-1">Código</th>
                        <th className="text-left py-1">Nombre</th>
                        <th className="text-left py-1">Tipo</th>
                        <th className="text-right py-1">Saldo</th>
                        <th className="text-center py-1">Problema</th>
                      </tr>
                    </thead>
                    <tbody>
                      {diagnostico.detalle.cuentasExcluidas.map(cuenta => (
                        <tr key={cuenta.id} className="border-b border-yellow-100">
                          <td className="py-1 font-mono text-xs">{cuenta.codigo}</td>
                          <td className="py-1">{cuenta.nombre}</td>
                          <td className="py-1">{cuenta.tipo}</td>
                          <td className="py-1 text-right font-medium">{formatCurrency(cuenta.saldo)}</td>
                          <td className="py-1 text-center text-xs">
                            {!cuenta.esCuentaMovimiento && <span className="text-red-600">No es cuenta mov.</span>}
                            {cuenta.estado !== 'activa' && <span className="text-red-600">Inactiva</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Todas las cuentas */}
              <div className="bg-white rounded-lg p-4 border">
                <h4 className="font-medium mb-3">Todas las Cuentas con Movimiento</h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <th className="text-left py-2 px-2">Código</th>
                        <th className="text-left py-2 px-2">Nombre</th>
                        <th className="text-left py-2 px-2">Tipo</th>
                        <th className="text-right py-2 px-2">Débitos</th>
                        <th className="text-right py-2 px-2">Créditos</th>
                        <th className="text-right py-2 px-2">Saldo</th>
                        <th className="text-center py-2 px-2">En Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {diagnostico.detalle.todasLasCuentas.map(cuenta => (
                        <tr key={cuenta.id} className={`border-b ${!cuenta.incluidoEnBalance ? 'bg-yellow-50' : ''}`}>
                          <td className="py-1 px-2 font-mono text-xs">{cuenta.codigo}</td>
                          <td className="py-1 px-2">{cuenta.nombre}</td>
                          <td className="py-1 px-2">{cuenta.tipo}</td>
                          <td className="py-1 px-2 text-right">{formatCurrency(cuenta.debitos)}</td>
                          <td className="py-1 px-2 text-right">{formatCurrency(cuenta.creditos)}</td>
                          <td className={`py-1 px-2 text-right font-medium ${cuenta.saldo >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {formatCurrency(cuenta.saldo)}
                          </td>
                          <td className="py-1 px-2 text-center">
                            {cuenta.incluidoEnBalance ? (
                              <CheckCircle2 className="h-4 w-4 text-green-500 inline" />
                            ) : (
                              <AlertTriangle className="h-4 w-4 text-yellow-500 inline" />
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="p-4 border-t bg-gray-50">
              <button
                onClick={() => setShowDiagnostico(false)}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg text-sm font-medium"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
